import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { IWorkHoursItem } from "../../../models/workHours/workHoursItem";
import {
    getUpToDateNormalWorkHours,
    getUpToDateWorkHours,
} from "../../../services/workHoursService";
import { Base } from "../../common/base";
import _groupBy from "lodash/groupBy";
import _map from "lodash/map";

export interface WorkHoursState {
    normal: IWorkHoursItem[];
    calculated: IWorkHoursItem[];
}

const initialState: WorkHoursState = {
    normal: [],
    calculated: [],
};

export const workHoursSlice = createSlice({
    name: "workHours",
    initialState,
    reducers: {
        workHoursReceived: (
            state,
            action: PayloadAction<{
                workHours: IWorkHoursItem[];
                startDate: string;
                endDate: string;
                normalOnly: boolean;
            }>
        ) => {
            let items = action.payload.normalOnly
                ? state.normal
                : state.calculated;

            // Preserve existing items outside of given range
            items = items.filter(
                (h) =>
                    h.dateStr < action.payload.startDate ||
                    h.dateStr > action.payload.endDate
            );
            items = [...items, ...action.payload.workHours];

            if (action.payload.normalOnly) {
                state.normal = items;
            } else {
                state.calculated = items;
            }
        },
    },
});

export const { workHoursReceived } = workHoursSlice.actions;

export const normalWorkHours = createAsyncThunk(
    "workHours/normalWorkHours",
    async(
        { startDate, endDate }: { startDate: string; endDate: string },
        thunkApi
    ) => {
        const workHoursItems = await getUpToDateNormalWorkHours(
            startDate,
            endDate,
            thunkApi.signal
        );
        thunkApi.dispatch(
            workHoursReceived({
                workHours: workHoursItems,
                startDate,
                endDate,
                normalOnly: true,
            })
        );
    }
);

export const calculatedWorkHours = createAsyncThunk(
    "workHours/calculatedWorkHours",
    async(
        {
            salaryPeriodId,
            startDate,
            endDate,
        }: { salaryPeriodId: string; startDate: string; endDate: string },
        thunkApi
    ) => {
        const workHoursItems = await getUpToDateWorkHours(
            salaryPeriodId,
            Base.isAfter(Base.dateStartOf(new Date(), "day"), endDate),
            thunkApi.signal
        );
        thunkApi.dispatch(
            workHoursReceived({
                workHours: workHoursItems,
                startDate,
                endDate,
                normalOnly: false,
            })
        );
    }
);

export const workHoursReducer = workHoursSlice.reducer;

const makeSelectWorkHoursByDate = (normal: boolean) =>
    createSelector(
        (state: WorkHoursState) => (normal ? state.normal : state.calculated),
        (_, rangeStart: string) => rangeStart,
        (_, _rangeStart, rangeEnd: string) => rangeEnd,
        (_, _rangeStart, _rangeEnd: string, categories?: string[]) => categories,
        (
            items: IWorkHoursItem[],
            rangeStart: string,
            rangeEnd: string,
            categories: string[]
        ) => {
            if (rangeStart && rangeEnd) {
                return items.filter(
                    (i) =>
                        (!categories || categories.includes(i.category)) &&
                        Base.isBetween(
                            i.dateStr,
                            rangeStart,
                            rangeEnd,
                            "day",
                            "[]"
                        )
                );
            }
        }
    );

const makeSelectWorkHoursAmountByDate = (normal: boolean) =>
    createSelector([makeSelectWorkHoursByDate(normal)], (items) =>
        items ? items.reduce((acc, cur) => acc + cur.amount, 0) : undefined
    );

const makeSelectWorkHoursByDateGroupedByCategory = (normal: boolean) =>
    createSelector([makeSelectWorkHoursByDate(normal)], (items) =>
        Base.groupArray(items, "categoryId")
    );

const makeSelectWorkHoursByDateGroupedByWorkShiftIdAndDate = (
    normal: boolean
) =>
    createSelector([makeSelectWorkHoursByDate(normal)], (items) =>
        _groupBy(items, (w) => `${w.workShiftId ?? "null"}_${w.dateStr}`)
    );

const makeSelectCalculatedWorkHoursByDateGroupedBySalaryRowTypeId = () =>
    createSelector([makeSelectWorkHoursByDate(false)], (items) =>
        Base.groupArray(items, "salaryRowTypeId")
    );

const makeSelectWorkHoursAmountByDateGroupedByCategory = (normal: boolean) =>
    createSelector(
        [makeSelectWorkHoursByDateGroupedByCategory(normal)],
        (grouped) =>
            Object.fromEntries(
                Object.keys(grouped).map((categoryId) => [
                    categoryId,
                    grouped[categoryId]?.reduce(
                        (acc, cur) => acc + cur.amount,
                        0
                    ),
                ])
            )
    );

const makeSelectWorkHoursAmountByDateGroupedByWorkShiftIdAndDate = (
    normal: boolean
) =>
    createSelector(
        [makeSelectWorkHoursByDateGroupedByWorkShiftIdAndDate(normal)],
        (grouped) =>
            Object.fromEntries(
                _map(grouped, (values, key) => [
                    key,
                    values.reduce((acc, cur) => acc + cur.amount, 0),
                ])
            )
    );

export const makeSelectCalculatedWorkHoursAmountByDateGroupedBySalaryRowTypeId =
    () =>
        createSelector(
            [makeSelectCalculatedWorkHoursByDateGroupedBySalaryRowTypeId()],
            (grouped) =>
                Object.fromEntries(
                    Object.keys(grouped).map((salaryRowTypeName) => [
                        salaryRowTypeName,
                        grouped[salaryRowTypeName]?.reduce(
                            (acc, cur) => acc + cur.amount,
                            0
                        ),
                    ])
                )
        );

export const makeSelectNormalWorkHoursByDate = () =>
    makeSelectWorkHoursByDate(true);
export const makeSelectCalculatedWorkHoursByDate = () =>
    makeSelectWorkHoursByDate(false);

export const makeSelectNormalWorkHoursAmountByDate = () =>
    makeSelectWorkHoursAmountByDate(true);
export const makeSelectCalculatedWorkHoursAmountByDate = () =>
    makeSelectWorkHoursAmountByDate(false);

export const makeSelectNormalWorkHoursAmountByDateGroupedByWorkShiftIdAndDate =
    () => makeSelectWorkHoursAmountByDateGroupedByWorkShiftIdAndDate(true);
export const makeSelectCalculatedWorkHoursAmountByDateGroupedByWorkShiftIdAndDate =
    () => makeSelectWorkHoursAmountByDateGroupedByWorkShiftIdAndDate(false);

export const makeSelectNormalWorkHoursAmountByDateGroupedByCategory = () =>
    makeSelectWorkHoursAmountByDateGroupedByCategory(true);
export const makeSelectCalculatedWorkHoursAmountByDateGroupedByCategory = () =>
    makeSelectWorkHoursAmountByDateGroupedByCategory(false);
